import {
  Box,
  Button,
  Heading,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Header } from "../../components/Header";
import { Page } from "../../components/Page";
import { Api } from "../../utils/Api";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { HiOutlineClock } from "react-icons/hi";
import { DataTable } from "../../components/Table";
import { Show } from "../../components/Show";
import {
  RequestData,
  Settlement,
  SettlementsFilter,
  SettlementStatus,
} from "../../utils/typings";
import moment from "moment";
import { formatNumber } from "../../utils/helpers";
import { Loader } from "../../components/Loader";
import { useError } from "../../hooks/useError";

const defaultRequestData: RequestData = {
  size: 10,
  page: 0,
  settlements: [],
};

export const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);

  const [tabIndex, setTabIndex] = useState(0);

  const [totalSettlementPages, setTotalSettlementPages] = useState(0);
  const [totalSettlements, setTotalSettlements] = useState(0);
  const [totalPendingSettlementPages, setTotalPendingSettlementPages] =
    useState(0);
  const [totalPendingSettlements, setTotalPendingSettlements] = useState(0);

  const [totalSettlementsCompleted, setTotalSettlementsCompleted] = useState(0);
  const [totalAmountTransferred, setTotalAmountTransferred] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(
    SettlementsFilter.DAYS_7
  );
  const [settlementData, setSettlementData] =
    useState<RequestData>(defaultRequestData);
  const [settlementsCompletedData, setSettlementsCompletedData] = useState<
    number[]
  >([]);

  const [allTimeSettlementData, setAllTimeSettlementData] =
    useState<RequestData>(defaultRequestData);

  const [amountReceivedData, setAmountReceivedData] = useState<number[]>([]);
  const [amountReceivedLabels, setAmountReceivedLabels] = useState([
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
  ]);
  const [settlementsCompletedLabels, setSettlementsCompletedLabels] = useState([
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
  ]);
  const [pendingSettlementData, setPendingSettlementData] =
    useState<RequestData>(defaultRequestData);

  const { handleError } = useError();

  useEffect(() => {
    init();
    getPendingSettlements(0, 9999);
    getAllTimeSettlementData({
      status: SettlementStatus.SETTLEMENT_COMPLETED,
    });
  }, []);

  useEffect(() => {
    getAllTimeSettlementData({
      status: SettlementStatus.SETTLEMENT_COMPLETED,
    });
  }, [pendingSettlementData]);

  useEffect(() => {
    setSettlementsCompletedData(getLastNDaysofSettlementsCompleted());
    setAmountReceivedData(getLastNDaysofAmountReceived());
  }, [selectedFilter, settlementData, allTimeSettlementData]);

  useEffect(() => {
    handleCompletedSettlements(settlementsCompletedData);
    handleTotalAmountTransferred(amountReceivedData);
  }, [settlementsCompletedData, amountReceivedData]);

  const getPendingSettlements = async (
    page?: number,
    size?: number,
    params?: any
  ) => {
    setIsLoading(true);
    const defaultParams = {
      sortFields: [
        {
          sortField: "initiatedAt",
          sortDirection: "DESC",
        },
      ],
    };
    const { data: pendingData } = await Api.getSettlements(
      page,
      99999,
      params || defaultParams
    );
    const settlements: Settlement[] = pendingData.settlements.filter(
      (settlement: Settlement) =>
        settlement.status !== SettlementStatus.SETTLEMENT_COMPLETED
    );
    setPendingSettlementData({
      ...pendingData,
      settlements,
    });
    setTotalPendingSettlementPages(pendingData.totalPages);
    setTotalPendingSettlements(settlements.length);
    setIsLoading(false);
  };

  const handleTabChange = (tabIndex: any) => {
    setTabIndex(tabIndex);
  };

  const getAllTimeSettlementData = async (incomingParams?: any) => {
    const defaultSortFields = [
      {
        sortField: "initiatedAt",
        sortDirection: "DESC",
      },
    ];
    const params = {
      ...incomingParams,
      sortFields: incomingParams?.sortFields || defaultSortFields,
    };
    const { data } = await Api.getSettlements(0, 99999, params);
    setAllTimeSettlementData(data);
  };

  const init = async (page?: number, size?: number, incomingParams?: any) => {
    try {
      setIsLoading(true);
      const defaultSortFields = [
        {
          sortField: "initiatedAt",
          sortDirection: "DESC",
        },
      ];
      const params = {
        ...incomingParams,
        sortFields: incomingParams?.sortFields || defaultSortFields,
      };
      const { data } = await Api.getSettlements(page, size, params);
      setTotalSettlements(data.totalElements);
      setTotalSettlementPages(data.totalPages);
      setSettlementData(data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysFromFilter = (filter: SettlementsFilter) => {
    switch (filter) {
      case SettlementsFilter.DAYS_7:
        return 7;
      case SettlementsFilter.DAYS_30:
        return 30;
      case SettlementsFilter.YTD:
        return 365;
      default:
        return 7;
    }
  };

  const getLastNDaysofSettlementsCompleted = () => {
    const n = getDaysFromFilter(selectedFilter);
    const tomorrow = moment().add(1, "d");
    const lastNDays: any[] = [];

    if (n >= 365) {
      Array(n)
        .fill(0)
        .map(() =>
          lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD YYYY"))
        );

      const numberOfMonthsInNDays = Math.ceil(lastNDays.length / 12);
      const monthsOfSettlements = Array(numberOfMonthsInNDays).fill(0);
      setSettlementsCompletedLabels(
        monthsOfSettlements
          .map((_week, index) =>
            moment().subtract(index, "month").format("MMMM")
          )
          .reverse()
      );

      const filteredSettlements = allTimeSettlementData?.settlements.filter(
        (settlement: Settlement) => {
          const date = moment(settlement.completedAt).format("MMM DD YYYY");
          if (lastNDays.includes(date)) return settlement;
          return false;
        }
      );

      filteredSettlements?.map((item: Settlement) => {
        monthsOfSettlements[
          monthsOfSettlements.length -
            1 -
            Math.ceil(
              lastNDays.indexOf(
                moment(item.completedAt).format("MMM DD YYYY")
              ) / 12
            )
        ] += 1;
        return false;
      });
      handleCompletedSettlements(monthsOfSettlements);
      return monthsOfSettlements;
    }

    if (n >= 30) {
      Array(n)
        .fill(0)
        .map(() =>
          lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD YYYY"))
        );

      const numberOfWeeksInNDays = Math.ceil(lastNDays.length / 7);
      const weeksOfSettlements = Array(numberOfWeeksInNDays).fill(0);
      setSettlementsCompletedLabels(
        weeksOfSettlements.map((_week, index) => `Week ${index + 1}`)
      );

      const filteredSettlements = allTimeSettlementData?.settlements.filter(
        (settlement: Settlement) => {
          const date = moment(settlement.completedAt).format("MMM DD YYYY");
          if (lastNDays.includes(date)) return settlement;
          return false;
        }
      );

      filteredSettlements?.map((item: Settlement) => {
        weeksOfSettlements[
          weeksOfSettlements.length -
            1 -
            Math.ceil(
              lastNDays.indexOf(
                moment(item.completedAt).format("MMM DD YYYY")
              ) / 7
            )
        ] += 1;
        return false;
      });
      handleCompletedSettlements(weeksOfSettlements);
      return weeksOfSettlements;
    }

    Array(n)
      .fill(0)
      .map(() => lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD")));
    setSettlementsCompletedLabels(lastNDays.reverse());

    const dayNSettlements = Array(n).fill(0);
    allTimeSettlementData?.settlements.filter((settlement: Settlement) => {
      const date = moment(settlement.completedAt).format("MMM DD");
      if (
        lastNDays.includes(date) &&
        settlement.status === SettlementStatus.SETTLEMENT_COMPLETED
      ) {
        const index = lastNDays.indexOf(date);
        dayNSettlements[index] += 1;
      }
      return false;
    });
    handleCompletedSettlements(dayNSettlements);
    return dayNSettlements;
  };

  const getLastNDaysofAmountReceived = () => {
    const n = getDaysFromFilter(selectedFilter);
    const tomorrow = moment().add(1, "d");
    const lastNDays: any[] = [];

    if (n >= 365) {
      Array(n)
        .fill(0)
        .map(() =>
          lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD YYYY"))
        );

      const numberOfMonthsInNDays = Math.ceil(lastNDays.length / 12);
      const monthsOfSettlements = Array(numberOfMonthsInNDays).fill(0);
      setAmountReceivedLabels(
        monthsOfSettlements
          .map((_week, index) =>
            moment().subtract(index, "month").format("MMMM")
          )
          .reverse()
      );

      const filteredSettlements = allTimeSettlementData?.settlements.filter(
        (settlement: Settlement) => {
          const date = moment(settlement.completedAt).format("MMM DD YYYY");
          if (lastNDays.includes(date)) return settlement;
          return false;
        }
      );

      filteredSettlements?.map((item: Settlement) => {
        monthsOfSettlements[
          monthsOfSettlements.length -
            1 -
            Math.ceil(
              lastNDays.indexOf(
                moment(item.completedAt).format("MMM DD YYYY")
              ) / 12
            )
        ] += item.receivedAmt;
        return false;
      });
      handleTotalAmountTransferred(monthsOfSettlements);
      return monthsOfSettlements;
    }

    if (n >= 30) {
      Array(n)
        .fill(0)
        .map(() =>
          lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD YYYY"))
        );

      const numberOfWeeksInNDays = Math.ceil(lastNDays.length / 7);
      const weeksOfSettlements = Array(numberOfWeeksInNDays).fill(0);
      setAmountReceivedLabels(
        weeksOfSettlements.map((_week, index) => `Week ${index + 1}`)
      );

      const filteredSettlements = allTimeSettlementData?.settlements.filter(
        (settlement: Settlement) => {
          const date = moment(settlement.completedAt).format("MMM DD YYYY");
          if (lastNDays.includes(date)) return settlement;
          return false;
        }
      );

      filteredSettlements?.map((item: Settlement) => {
        weeksOfSettlements[
          weeksOfSettlements.length -
            1 -
            Math.ceil(
              lastNDays.indexOf(
                moment(item.completedAt).format("MMM DD YYYY")
              ) / 7
            )
        ] += item.receivedAmt;
        return false;
      });
      handleTotalAmountTransferred(weeksOfSettlements);
      return weeksOfSettlements;
    }

    Array(n)
      .fill(0)
      .map(() => lastNDays.push(tomorrow.subtract(1, "d").format("MMM DD")));
    setAmountReceivedLabels(lastNDays.reverse());

    const dayNSettlements = Array(n).fill(0);
    allTimeSettlementData?.settlements.filter((settlement: Settlement) => {
      const date = moment(settlement.completedAt).format("MMM DD");
      if (lastNDays.includes(date)) {
        const index = lastNDays.indexOf(date);
        dayNSettlements[index] += Math.floor(settlement.receivedAmt);
      }
      return false;
    });
    handleTotalAmountTransferred(dayNSettlements);
    return dayNSettlements;
  };

  const handleTotalAmountTransferred = (settlementAmounts: number[]) => {
    const amount = settlementAmounts.reduce(
      (prev: number, curr: number) => (prev || 0) + (curr || 0),
      0
    );
    setTotalAmountTransferred(amount);
  };

  const handleCompletedSettlements = (settlementAmounts: number[]) => {
    const amount = settlementAmounts.reduce(
      (prev: number, curr: number) => prev + curr,
      0
    );
    setTotalSettlementsCompleted(amount);
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        position: "right" as const,
        grid: {
          display: false,
        },
        ticks: {
          callback: (label: any) => formatNumber(label, "none"),
        },
      },
    },
  };

  const settlementsCompletedGraphData = {
    labels: settlementsCompletedLabels,
    datasets: [
      {
        label: "y",
        data: settlementsCompletedData,
        backgroundColor: "#54565A",
      },
    ],
  };

  const amountReceivedGraphData = {
    labels: amountReceivedLabels,
    datasets: [
      {
        label: "y",
        data: amountReceivedData,
        backgroundColor: "#54565A",
      },
    ],
  };

  const handleFilterChange = (filter: SettlementsFilter) => {
    setSelectedFilter(filter);
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <Page
        title="Dashboard"
        display="flex"
        showHeader={false}
        bg="white"
        color="#54565A"
      >
        <Show condition={tabIndex === 0}>
          <Box
            top="0"
            left="0"
            w="full"
            h="37%"
            pos="absolute"
            bg="black"
            borderRadius="0px 0px 15px 15px"
          />
        </Show>
        <Tabs onChange={handleTabChange} w="full" index={tabIndex}>
          <Header
            pos="relative"
            currentTab={tabIndex}
            onTabChange={handleTabChange}
          />
          <TabPanels pos="relative">
            <TabPanel m={{ base: "50px 0px", lg: "50px 30px" }}>
              <Stack
                w="full"
                justify="space-between"
                direction={{ base: "column", sm: "row" }}
                mb="16px"
              >
                <Heading color="white">Overview</Heading>
                <Stack direction="row" scrollBehavior="auto">
                  <Button
                    variant={
                      selectedFilter === SettlementsFilter.DAYS_7
                        ? "secondary"
                        : "tiertary"
                    }
                    borderRadius="40px"
                    onClick={() => handleFilterChange(SettlementsFilter.DAYS_7)}
                  >
                    7 days
                  </Button>
                  <Button
                    variant={
                      selectedFilter === SettlementsFilter.DAYS_30
                        ? "secondary"
                        : "tiertary"
                    }
                    borderRadius="40px"
                    onClick={() =>
                      handleFilterChange(SettlementsFilter.DAYS_30)
                    }
                  >
                    30 days
                  </Button>
                  <Button
                    variant={
                      selectedFilter === SettlementsFilter.YTD
                        ? "secondary"
                        : "tiertary"
                    }
                    borderRadius="40px"
                    onClick={() => handleFilterChange(SettlementsFilter.YTD)}
                  >
                    This year
                  </Button>
                </Stack>
              </Stack>
              <Stack
                direction={{ base: "column", lg: "row" }}
                w="full"
                spacing="19px"
              >
                <Stack
                  bg="#F6F2F8"
                  p="20px"
                  w="full"
                  borderRadius="12px"
                  justify="space-between"
                >
                  <Text fontWeight={400} fontSize="20px">
                    Total amount transferred
                  </Text>
                  <Stack
                    justify="space-between"
                    direction={{ base: "column", md: "row" }}
                    alignItems={{ base: "start", md: "end" }}
                  >
                    <Heading
                      height="fit-content"
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      color="#54565A"
                    >
                      {formatNumber(
                        totalAmountTransferred,
                        settlementData?.settlements[0]?.targetFiatCcy
                      )}
                    </Heading>
                    <Stack w={{ base: "full", md: "75%" }}>
                      <Bar data={amountReceivedGraphData} options={options} />
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  bg="#E0E7B8"
                  p="20px"
                  w="full"
                  borderRadius="12px"
                  justify="space-between"
                >
                  <Text fontWeight={400} fontSize="20px">
                    Number of settlements completed
                  </Text>
                  <Stack
                    justify="space-between"
                    direction={{ base: "column", md: "row" }}
                    alignItems={{ base: "start", md: "end" }}
                  >
                    <Heading
                      height="fit-content"
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      color="#54565A"
                    >
                      {totalSettlementsCompleted}
                    </Heading>
                    <Stack w={{ base: "full", md: "75%" }}>
                      <Bar
                        data={settlementsCompletedGraphData}
                        options={options}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                w="full"
                justify="space-between"
                alignItems="center"
                spacing="19px"
                mt={3}
              >
                <Stack direction="row" alignItems="center">
                  <HiOutlineClock color="#54565A" size="30px" />
                  <Text fontSize="30px" fontWeight="400" color="#54565A">
                    {pendingSettlementData?.settlements?.length || 0} Pending
                    settlements
                  </Text>
                </Stack>
                <Button
                  variant="secondary"
                  onClick={() => setTabIndex(1)}
                  textOverflow="ellipsis"
                >
                  View all
                </Button>
              </Stack>
              <DataTable
                data={pendingSettlementData}
                totalRows={totalPendingSettlements}
                totalPages={totalPendingSettlementPages}
                fetchData={getPendingSettlements}
              />
            </TabPanel>
            <TabPanel>
              <DataTable
                showFilters
                showEdit
                showSorting
                showCSV
                showCreate
                data={settlementData}
                totalRows={totalSettlements}
                totalPages={totalSettlementPages}
                fetchData={init}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Page>
    </>
  );
};
